import { useCallback } from "react";
import { useAuthStore } from "../stores/auth-store";

// ============================================================================
// Types
// ============================================================================

interface LoginParams {
  email: string;
  password: string;
}

interface SignupParams {
  name: string;
  email: string;
  password: string;
}

interface ApiResponse<T> {
  success: boolean;
  data: T;
  error?: {
    code: string;
    message: string;
    details?: unknown[];
  };
}

// ============================================================================
// Helpers
// ============================================================================

import { getBaseUrl } from "../api-client";

async function authFetch<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const url = `${getBaseUrl()}/platform/auth${endpoint}`;

  const response = await fetch(url, {
    ...options,
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      ...options.headers,
    },
  });

  const json = (await response.json()) as ApiResponse<T>;

  if (!response.ok || !json.success) {
    const message =
      json.error?.message || `Request failed with status ${response.status}`;
    throw new Error(message);
  }

  return json.data;
}

// ============================================================================
// Hook
// ============================================================================

export function useAuth() {
  // Read reactive state for rendering
  const user = useAuthStore((s) => s.user);
  const tenant = useAuthStore((s) => s.tenant);
  const currentCompanyId = useAuthStore((s) => s.currentCompanyId);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const isLoading = useAuthStore((s) => s.isLoading);

  // Fetch current user from /auth/me (cookie-based)
  // Uses getState() so this callback never changes reference
  const fetchMe = useCallback(async () => {
    const store = useAuthStore.getState();
    try {
      store.setLoading(true);

      const data = await authFetch<{
        user: {
          id: string;
          email: string;
          name: string;
          avatarUrl?: string | null;
          isPlatformAdmin: boolean;
          createdAt?: string;
        };
        tenant: {
          id: string;
          name: string;
          slug: string;
          status: string;
          role: string;
        } | null;
      }>("/me");

      store.setUser(data.user);
      store.setTenant(data.tenant);
    } catch {
      // Not authenticated or token expired — clear state silently
      store.setUser(null);
      store.setTenant(null);
    } finally {
      store.setLoading(false);
    }
  }, []);

  // Login
  const login = useCallback(async (params: LoginParams) => {
    const data = await authFetch<{
      user: {
        id: string;
        email: string;
        name: string;
        avatarUrl?: string | null;
        isPlatformAdmin: boolean;
      };
      tenantId: string | null;
      token: string;
    }>("/login", {
      method: "POST",
      body: JSON.stringify(params),
    });

    const store = useAuthStore.getState();
    store.setUser(data.user);

    // Fetch full user data (with tenant) after login
    await fetchMe();

    return { tenantId: data.tenantId };
  }, [fetchMe]);

  // Signup
  const signup = useCallback(async (params: SignupParams) => {
    const data = await authFetch<{
      user: {
        id: string;
        email: string;
        name: string;
      };
      tenant?: {
        id: string;
        name: string;
        slug: string;
      } | null;
      token: string;
    }>("/signup", {
      method: "POST",
      body: JSON.stringify(params),
    });

    // Fetch full user data after signup
    await fetchMe();

    return {
      user: data.user,
      tenant: data.tenant ?? null,
    };
  }, [fetchMe]);

  // Logout
  const logout = useCallback(async () => {
    try {
      await authFetch("/logout", { method: "POST" });
    } catch {
      // Ignore errors — we clear local state regardless
    } finally {
      useAuthStore.getState().reset();
      if (typeof window !== "undefined") {
        window.location.href = "/login";
      }
    }
  }, []);

  // Switch company
  const switchCompany = useCallback((companyId: string) => {
    useAuthStore.getState().setCurrentCompany(companyId);
  }, []);

  return {
    // Reactive state
    user,
    tenant,
    currentCompanyId,
    isAuthenticated,
    isLoading,

    // Stable actions (never change reference)
    login,
    signup,
    logout,
    fetchMe,
    switchCompany,
  };
}
